import { Text } from '@thanx/uikit/text'
import Lock from 'components/Icons/Lock'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import palette from 'constants/palette'
import { usePermissionReason } from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'
import { Fields as UserProfile } from 'models/UserProfile'
import React from 'react'
import { useSelector } from 'react-redux'
import { selectTiers } from 'selectors/tier'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import useProfilePermissions from './useProfilePermissions'

const t = buildTranslate('users.view')

const BADGE_CLASS_NAME = 'mb-m d-inline-flex align-items-center'
const badgeBoxStyle = {
  borderRadius: '4px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: merchantTheme.colors.grey30,
}

type PropsT = {
  profile: UserProfile
}

const TierBadge: React.FC<PropsT> = ({ profile }) => {
  const [css] = useStyletron()
  const { current_tier } = profile
  const tiers = useSelector(state => selectTiers(state))
  // Without tiers access we can't resolve the tier image, so a customer who
  // has a tier gets a disabled, locked badge (name still comes from the profile).
  const { canAccessTiers } = useProfilePermissions()
  const permissionReason = usePermissionReason('TiersProgram', !canAccessTiers)

  if (!current_tier) return null

  if (!canAccessTiers) {
    return (
      <PermissionTooltip
        disabled
        reason={permissionReason}
        id="tier-badge-perm"
        inline
        srOnlyReason
      >
        <span
          className={`${BADGE_CLASS_NAME} p-s ${css({
            ...badgeBoxStyle,
            backgroundColor: palette.grey05,
          })}`}
        >
          <span
            aria-hidden="true"
            className={`d-flex align-items-center justify-content-center ${css({
              width: '32px',
              height: '32px',
            })}`}
          >
            <Lock fillColor={palette.grey70} />
          </span>
          <span className={'d-block ml-xs'}>
            <Text.SmallCaps3
              tag="span"
              className={`d-block m-0 font-size-10 bold ${css({
                lineHeight: '12px',
              })}`}
              color="grey70"
            >
              {t('tier')}
            </Text.SmallCaps3>
            <Text.BodyText3
              tag="span"
              className={'d-block font-weight-normal m-0'}
              color="grey70"
            >
              {current_tier}
            </Text.BodyText3>
          </span>
          {!permissionReason && (
            <span className="sr-only">{t('tier_restricted')}</span>
          )}
        </span>
      </PermissionTooltip>
    )
  }

  const selectedTier = tiers.find(tier => tier.name === current_tier)
  if (!selectedTier) return null

  return (
    <div
      className={`${BADGE_CLASS_NAME} grey-10-bkg p-s ${css(badgeBoxStyle)}`}
    >
      <div className={'align-items-center'}>
        <img
          src={selectedTier.icon_image_urls?.large}
          alt={t('background_image')}
          className={`${css({
            objectFit: 'cover',
            width: '32px',
            height: '32px',
          })}`}
        />
      </div>
      <div className={'d-block ml-xs'}>
        <Text.SmallCaps3
          tag="p"
          className={`m-0 font-size-10 bold ${css({
            lineHeight: '12px',
          })}`}
        >
          {t('tier')}
        </Text.SmallCaps3>
        <Text.BodyText3
          tag="p"
          className={'font-weight-normal m-0'}
          color="grey70"
        >
          {current_tier}
        </Text.BodyText3>
      </div>
    </div>
  )
}

export default TierBadge
